import { AnimatePresence, LayoutGroup } from 'framer-motion';
import { rgba } from 'polished';
import { useState } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { tokens } from '@treams/treams-ui';

import { cellPlacements as sortCellPlacements } from '../talentReviewCellOrder';
import type { TalentReviewCellColors } from '../talentReviewColors';
import { useTalentReviewInsertion } from '../talentReviewInsertion';
import { useTalentReviewPresence } from '../talentReviewPresence';
import { isOutdated } from '../talentReviewSidebarOrder';
import TalentReviewAxisLabel from './TalentReviewAxisLabel';
import TalentReviewPerson from './TalentReviewPerson';
import TalentReviewSwirl from './TalentReviewSwirl';
import { TalentReviewTooltip } from './TalentReviewTooltip';

const CellOutline = styled(Box)<{
	hex: string;
	$labelHex: string;
	hover?: boolean;
	$raised?: boolean;
}>`
	border-radius: ${tokens.radius.sm}px;
	border: 1.5px solid ${(props) => props.hex};
	background: ${(props) => props.theme.colors.white};
	height: 140px;
	position: relative;
	/* Above the sibling cells while an avatar animates in, so it is not drawn underneath them. */
	${(props) => props.$raised && 'z-index: 2;'}
	color: ${(props) => props.$labelHex};
	transition: background 100ms ease-in-out;

	/* While a person hovers over the cell, tint it with its own colour. */
	${(props) => props.hover && `background: ${rgba(props.hex, 0.1)};`}
`;

/* Clips the decoration to the rounded cell without clipping the title or lifted people. */
const Decoration = styled.div`
	position: absolute;
	inset: 0;
	overflow: hidden;
	border-radius: inherit;
	pointer-events: none;
`;

const Swirl = styled(TalentReviewSwirl)<{ hex: string }>`
	position: absolute;
	top: -10px;
	right: 28px;
	width: 190px;
	height: auto;
	color: ${(props) => props.hex};
`;

const CellTitle = styled.div`
	position: absolute;
	left: 50%;
	top: 100%;
	transform: translate(-50%, -50%);
	z-index: 1;
	background: ${tokens.surface.white};
	border-radius: ${tokens.radius.full}px;
	white-space: nowrap;
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.medium};
	line-height: ${tokens.typography.lineHeights.body};
`;

/* The dotted underline hints that hovering the words reveals the cell's instruction. It sits on
   the tooltip's inline-block container, as text-decoration does not propagate into one. */
const InstructionTooltip = styled(TalentReviewTooltip)`
	text-decoration: underline dotted;
	text-underline-offset: 4px;
	cursor: help;
`;

const CellContainer = styled(Box)<{
	hex: string;
	hover?: boolean;
	$focus?: boolean;
	$dragging?: boolean;
}>`
	width: 100%;
	height: 140px;
	position: relative;
	/* Visible while a person is focused or an avatar animates in from another cell. */
	overflow-x: ${(props) => (props.$focus ? 'visible' : 'hidden')};
	/* While dragging, the dnd placeholder can push content past the cell height; hide the scrollbar. */
	overflow-y: ${(props) => (props.$focus ? 'visible' : props.$dragging ? 'hidden' : 'auto')};
	padding: 8px;
	box-sizing: border-box;
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	align-content: flex-start;
	justify-content: flex-start;
	gap: 8px;
`;

export default function TalentReviewCell(props: {
	row: number;
	column: number;
	readonly?: boolean;
	dragging?: boolean;
	cell: graphql.TalentReviewTemplateCell;
	/** Border, label and swirl colours; fixed by the cell's position in the grid. */
	colors: TalentReviewCellColors;
	/** The template's threshold; older placements get the warning ring. */
	outdatedAfterDays: number;
	placements: graphql.TalentReviewGridPlacement[];
	/** Isolates this person on the grid and fades everyone else. */
	focusUserID?: string;
	/** Shows the selection ring without isolating. */
	highlightUserID?: string;
	onSelectUser?: (userID: string) => void;
	/** Draws the brand swirl in the cell corner (used for the top-right cell). */
	decorated?: boolean;
}) {
	const { localize } = useLocalize();
	const presence = useTalentReviewPresence();
	const insertion = useTalentReviewInsertion();

	// localize() falls back to an entry without text, so check the text itself.
	const instruction = localize(props.cell.instruction)?.text;
	const label = localize(props.cell.label)?.text;

	const hex = props.colors.border;
	const labelHex = props.colors.label;
	// In cell order (see talentReviewCellOrder). The server returns placements in
	// an unrelated order (last changed first); sorting here keeps the optimistic
	// order and the saved order identical.
	const cellPlacements = sortCellPlacements(props.placements, props.row, props.column);
	const focusedPlacement = cellPlacements.find(({ user }) => user.id === props.focusUserID);
	const droppableId = `cell-${props.row}-${props.column}`;

	// While a person is dragged over this cell: the avatar in front of which they
	// would be inserted opens a slot (or the last one opens it behind itself).
	// The index counts the others only: the dragged avatar is out of the flow.
	const others = insertion
		? cellPlacements.filter(({ user }) => user.id !== insertion.draggableId)
		: cellPlacements;
	const slotIndex =
		insertion?.target?.droppableId === droppableId ? insertion.target.index : undefined;
	const slotBeforeUserID = slotIndex !== undefined ? others[slotIndex]?.user.id : undefined;
	const slotAfterUserID =
		slotIndex !== undefined && slotIndex >= others.length
			? others[others.length - 1]?.user.id
			: undefined;

	// People whose avatar is animating into this cell (e.g. after switching
	// dates). The cell stops clipping and rises above its siblings meanwhile.
	const [animatingUserIDs, setAnimatingUserIDs] = useState<Set<string>>(() => new Set());
	const animating = cellPlacements.some(({ user }) => animatingUserIDs.has(user.id));
	const setAnimating = (userID: string, active: boolean) =>
		setAnimatingUserIDs((current) => {
			if (current.has(userID) === active) {
				return current;
			}
			const next = new Set(current);
			if (active) {
				next.add(userID);
			} else {
				next.delete(userID);
			}
			return next;
		});

	return (
		<Box marginLeft={10} flex={1} minWidth={0}>
			<Droppable
				droppableId={droppableId}
				type="USER"
				direction="horizontal"
				isDropDisabled={props.readonly}
			>
				{(provided, snapshot) => (
					<CellOutline
						hex={hex}
						$labelHex={labelHex}
						hover={snapshot.isDraggingOver}
						$raised={animating}
					>
						{props.decorated && (
							<Decoration>
								<Swirl hex={props.colors.soft} />
							</Decoration>
						)}
						{/* The droppable is the padded container so the drop animation lands inside the padding. */}
						<CellContainer
							ref={provided.innerRef}
							{...provided.droppableProps}
							hex={hex}
							hover={snapshot.isDraggingOver}
							$focus={!!focusedPlacement || animating}
							$dragging={props.dragging}
						>
							<LayoutGroup>
								{/* `custom` tells a leaving avatar whether its person is still on the page.
								    No `initial={false}` here: people entering on the first render (the
								    grid opening) grow in too; the others opt out via their own `initial`. */}
								<AnimatePresence custom={presence.present}>
									{cellPlacements.map((placement, index) => {
										const { user } = placement;

										return (
											<TalentReviewPerson
												key={user.id}
												user={user}
												index={index}
												dragging={props.dragging}
												focused={user.id === props.focusUserID}
												highlighted={user.id === props.highlightUserID}
												outdated={isOutdated(placement, props.outdatedAfterDays)}
												dimmed={!!props.focusUserID && user.id !== props.focusUserID}
												dragDisabled={!!props.focusUserID && user.id !== props.focusUserID}
												onSelect={
													props.onSelectUser ? () => props.onSelectUser!(user.id) : undefined
												}
												readonly={props.readonly}
												droppableId={droppableId}
												onLayoutAnimation={(active) => setAnimating(user.id, active)}
												entering={presence.entering.has(user.id)}
												slotBefore={user.id === slotBeforeUserID}
												slotAfter={user.id === slotAfterUserID}
											/>
										);
									})}
								</AnimatePresence>
							</LayoutGroup>
							{/* dnd's placeholder is not used: it only knows a single axis and the
							    cell wraps. The slot is opened by the avatars themselves (see above). */}
							<Box style={{ display: 'none' }}>{provided.placeholder}</Box>
						</CellContainer>
						{label ? (
							<CellTitle>
								<TalentReviewAxisLabel
									text={props.cell.label}
									wrapText={
										instruction
											? (text) => (
													<InstructionTooltip
														content={instruction}
														enabled={!props.dragging}
														position="bottom"
														fitContent
														portal
													>
														{text}
													</InstructionTooltip>
												)
											: undefined
									}
								/>
							</CellTitle>
						) : undefined}
					</CellOutline>
				)}
			</Droppable>
		</Box>
	);
}
